export interface UnavailabilityReasons {
  suspended?: boolean;
  deleted?: boolean;
  outOfPerimeter?: boolean;
  outOfPurchaseFamily?: boolean;
  removed?: boolean;
}

export function hasUnavailabilityReasons(reasons?: UnavailabilityReasons): boolean {
  if (!reasons) return false;
  return Object.values(reasons).some(reason => reason === true);
}

export function isItemAvailable(item: { unavailabilityReasons?: UnavailabilityReasons }): boolean {
  return !hasUnavailabilityReasons(item.unavailabilityReasons);
}

export function getUnavailabilityReasonsList(reasons?: UnavailabilityReasons): string[] {
  if (!reasons) return [];

  const activeReasons: string[] = [];

  if (reasons.suspended) activeReasons.push('suspended');
  if (reasons.deleted) activeReasons.push('deleted');
  if (reasons.outOfPerimeter) activeReasons.push('outOfPerimeter');
  if (reasons.outOfPurchaseFamily) activeReasons.push('outOfPurchaseFamily');
  if (reasons.removed) activeReasons.push('removed');

  return activeReasons;
}

export function canItemBeRestored(item: { unavailabilityReasons?: UnavailabilityReasons }): boolean {
  if (!item.unavailabilityReasons) return true;

  // Un item peut être restauré seulement si suspended est sa seule raison d'indisponibilité
  const { suspended, ...otherReasons } = item.unavailabilityReasons;
  const hasOtherReasons = Object.values(otherReasons).some(reason => reason === true);

  return suspended === true && !hasOtherReasons;
}

export function getDisplayStatusMessage(
  item: { unavailabilityReasons?: UnavailabilityReasons },
  statusLabels?: Record<string, string>,
): string[] {
  if (!hasUnavailabilityReasons(item.unavailabilityReasons)) {
    return [];
  }

  const reasons = getUnavailabilityReasonsList(item.unavailabilityReasons);
  const defaultLabels = {
    suspended: 'Suspendu',
    deleted: 'Supprimé',
    outOfPerimeter: 'Hors périmètre',
    outOfPurchaseFamily: 'Hors du périmètre de la famille achats',
  };

  const labels = { ...defaultLabels, ...statusLabels };
  return reasons.map(reason => labels[reason] || reason);
}

/**
 * Obtient le statut d'affichage pour un item de panier
 * @param item L'item avec un statut et éventuellement des raisons d'indisponibilité
 * @param statusLabels Libellés personnalisés pour les raisons d'indisponibilité
 * @returns Le statut à afficher (raisons d'indisponibilité ou statut normal)
 */
export function getItemDisplayStatus(
  item: { status?: string; unavailabilityReasons?: UnavailabilityReasons },
  statusLabels?: Record<string, string>,
): string | string[] {
  const unavailabilityMessages = getDisplayStatusMessage(item, statusLabels);

  if (unavailabilityMessages.length > 0) {
    return unavailabilityMessages.length === 1 ? unavailabilityMessages[0] : unavailabilityMessages;
  }

  return item.status || '';
}
